import { useMemo } from 'react'

import { t } from 'i18next'

import { useCardVisibility } from '~/components/CardSelector'
import {
  formatMoney,
  formatNumberWithDots,
  getAccountId,
  isAdvertiser,
  isPublisher
} from '~/helpers'
import { ClickCutoffAlertIcon } from '~/modules/retailMedia/components/ClickCutoffAlertIcon'
import { ConversionRateCutoffAlertIcon } from '~/modules/retailMedia/components/ConversionRateCutoffAlertIcon'
import { ConversionsCutoffAlertIcon } from '~/modules/retailMedia/components/ConversionsCutoffAlertIcon'
import { TitleWithTooltip } from '~/modules/retailMedia/components/DashboardRetailMediaCampaigns/cards/TitleWithTooltip'
import { ImpressionShareAlertIcon } from '~/modules/retailMedia/components/ImpressionShareAlertIcon'
import { LostIsBudgetAlertIcon } from '~/modules/retailMedia/components/LostIsBudgetAlertIcon'
import { LostIsRankAlertIcon } from '~/modules/retailMedia/components/LostIsRankAlertIcon'
import { RoasMetricCell } from '~/modules/retailMedia/components/RoasMetricCell'
import { getNtbOrdersWindow } from '~/modules/retailMedia/helpers/newMetricsAvailability'
import { buildNtbCards } from '~/modules/retailMedia/helpers/ntbCards'
import {
  buildNtbTooltipValues,
  getNtbBadgeLabel,
  NTB_METRICS
} from '~/modules/retailMedia/helpers/ntbMetrics'
import { useClickMetricsCutoffAlert } from '~/modules/retailMedia/hooks/useClickMetricsCutoffAlert'
import { useNtbAvailability } from '~/modules/retailMedia/hooks/useNtbAvailability'

const DEFAULT_VISIBLE: AvailableCards[] = [
  'impressions',
  'clicks',
  'ctr',
  'totalSpent',
  'income',
  'roas',
  'adCost'
]

const STANDARD_EXCLUSIVE_CARDS = new Set<AvailableCards>([
  'roas',
  'roasClick',
  'roasHalo',
  'roasOverall',
  'conversionRateClick',
  'conversionsClick',
  'salesClick'
])

// card → métrica que precisa estar disponível na resposta para ele existir
const CARD_AVAILABILITY_KEY: Partial<
  Record<AvailableCards, keyof NewMetricsAvailability>
> = {
  impressionShare: 'impressionShare',
  lostIsRank: 'lostIsRank',
  lostIsBudget: 'lostIsBudget',
  ...Object.fromEntries(
    NTB_METRICS.map(metric => [metric.key, metric.availabilityKey])
  )
}

const CARDS_BY_AD_TYPE: Record<CampaignAdType, CardsByAdType> = {
  product: { type: 'all' },
  sponsored_brand: { type: 'all' },
  banner: { type: 'all' },
  banner_video: { type: 'all' },
  sponsored_brand_video: { type: 'all' },
  digital_signage: { type: 'subset', cards: ['impressions'] },
  banner_off_site: { type: 'all' }
}

const formatCurrencyValue = (value?: string | number) => {
  const parsed = Number(value)

  if (Number.isNaN(parsed)) return value || '-'

  return formatMoney(parsed)
}

export function useCards({
  data,
  compareData,
  strategy,
  adType,
  campaignAdType,
  campaignPublisherId,
  endDate,
  lookbackMonths
}: UseCardsProps) {
  const strategyType = strategy || 'standard'
  const isStandardStrategy = strategyType === 'standard'
  const showClickAlert =
    useClickMetricsCutoffAlert({ checkCompare: true }) && isStandardStrategy

  const availabilitySources = useMemo(
    () => [data, compareData],
    [data, compareData]
  )
  // a família de IS segue o entitlement: período sem dado não é "não habilitado"
  const availableMetrics = useNtbAvailability(availabilitySources, {
    impressionShare: {
      publisherId: isPublisher() ? getAccountId() : campaignPublisherId,
      advertiserId: isAdvertiser() ? getAccountId() : null,
      fromEntitlement: true
    }
  })

  const ntbOrdersWindow = useMemo(() => getNtbOrdersWindow(endDate), [endDate])
  // mesmo lookback (URL) que a tabela de anúncios desta tela envia à API
  const ntbCards = useMemo(
    () =>
      buildNtbCards({
        availableMetrics,
        formatted: data?.formatted,
        compareFormatted: compareData?.formatted,
        tooltipValues: buildNtbTooltipValues({
          lookbackMonths,
          ordersWindow: ntbOrdersWindow
        }),
        badge: getNtbBadgeLabel(ntbOrdersWindow),
        typeOf: metric => metric.key
      }),
    [availableMetrics, data, compareData, lookbackMonths, ntbOrdersWindow]
  )

  const cardsRaw: CardItem[] = useMemo(
    () => [
      {
        id: 'card-01',
        label: t('rm:metrics.impressions'),
        title: <TitleWithTooltip metric="impressions" />,
        value: data?.formatted?.impressions || 0,
        compareValue: compareData?.formatted?.impressions,
        type: 'impressions'
      },
      {
        id: 'card-08',
        label: t('rm:metrics.views'),
        title: <TitleWithTooltip metric="views" />,
        value: formatNumberWithDots(data?.views || 0),
        compareValue: compareData
          ? formatNumberWithDots(compareData?.views || 0)
          : undefined,
        type: 'views'
      },
      {
        id: 'card-02',
        label: t('rm:metrics.clicks'),
        title: (
          <TitleWithTooltip
            metric="clicks"
            tooltipMetric={
              campaignAdType === 'banner_off_site' ? 'clicksOffsite' : undefined
            }
          />
        ),
        value: data?.formatted?.clicks || 0,
        compareValue: compareData?.formatted?.clicks,
        type: 'clicks'
      },
      {
        id: 'card-03',
        label: t('rm:metrics.ctr'),
        title: <TitleWithTooltip metric="ctr" />,
        value: data?.formatted?.ctr || 0,
        compareValue: compareData?.formatted?.ctr,
        type: 'ctr'
      },
      {
        id: 'card-04',
        label: isPublisher()
          ? t('rm:metrics.adsRevenue')
          : t('rm:metrics.investment'),
        title: (
          <TitleWithTooltip
            metric={isPublisher() ? 'adsRevenue' : 'investment'}
          />
        ),
        value: data?.formatted?.total_spent || 0,
        compareValue: compareData?.formatted?.total_spent,
        type: 'totalSpent'
      },
      {
        id: 'card-09',
        label: t('rm:metrics.conversions'),
        title: (
          <TitleWithTooltip
            metric="conversions"
            tooltipMetric={
              isStandardStrategy ? undefined : 'conversionsObjectiveBased'
            }
            alertIcon={
              isStandardStrategy && <ConversionsCutoffAlertIcon checkCompare />
            }
          />
        ),
        value: data?.formatted?.conversions || 0,
        compareValue: compareData?.formatted?.conversions,
        type: 'conversions'
      },
      {
        id: 'card-conversions_click',
        label: t('rm:metrics.conversionsClick'),
        title: (
          <TitleWithTooltip
            metric="conversionsClick"
            alertIcon={showClickAlert && <ClickCutoffAlertIcon />}
          />
        ),
        value: data?.formatted?.conversions_click || '-',
        compareValue: compareData?.formatted?.conversions_click,
        type: 'conversionsClick'
      },
      {
        id: 'card-10',
        label: t('rm:metrics.conversion_rate'),
        title: (
          <TitleWithTooltip
            metric="conversion_rate"
            tooltipMetric={
              isStandardStrategy ? undefined : 'conversionRateObjectiveBased'
            }
            alertIcon={
              isStandardStrategy && (
                <ConversionRateCutoffAlertIcon checkCompare />
              )
            }
          />
        ),
        value: data?.formatted?.conversion_rate || 0,
        compareValue: compareData?.formatted?.conversion_rate,
        type: 'conversionRate'
      },
      {
        id: 'card-conversion_rate_click',
        label: t('rm:metrics.conversionRateClick'),
        title: (
          <TitleWithTooltip
            metric="conversionRateClick"
            alertIcon={showClickAlert && <ClickCutoffAlertIcon />}
          />
        ),
        value: data?.formatted?.conversion_rate_click || '-',
        compareValue: compareData?.formatted?.conversion_rate_click,
        type: 'conversionRateClick'
      },
      {
        id: 'card-05',
        label: t('rm:metrics.sales'),
        title: <TitleWithTooltip metric="sales" />,
        value: data?.formatted?.income || 0,
        compareValue: compareData?.formatted?.income,
        type: 'income'
      },
      {
        id: 'card-sales_click',
        label: t('rm:metrics.salesClick'),
        title: (
          <TitleWithTooltip
            metric="salesClick"
            alertIcon={showClickAlert && <ClickCutoffAlertIcon />}
          />
        ),
        value: data?.formatted?.income_click || '-',
        compareValue: compareData?.formatted?.income_click,
        type: 'salesClick'
      },
      {
        id: 'card-06',
        label: t('rm:metrics.roas'),
        title: <TitleWithTooltip metric="roas" />,
        value: (
          <RoasMetricCell
            metric="roas"
            value={data?.formatted?.roas || 0}
            raw={data}
          />
        ),
        compareValue: compareData && (
          <RoasMetricCell
            metric="roas"
            value={compareData.formatted?.roas}
            raw={compareData}
          />
        ),
        type: 'roas'
      },
      {
        id: 'card-12',
        label: t('rm:metrics.roasClick'),
        title: (
          <TitleWithTooltip
            metric="roasClick"
            alertIcon={showClickAlert && <ClickCutoffAlertIcon />}
          />
        ),
        value: data?.formatted?.roas_click || '-',
        compareValue: compareData?.formatted?.roas_click,
        type: 'roasClick'
      },
      {
        id: 'card-roas_halo',
        label: t('rm:metrics.roasHalo'),
        title: <TitleWithTooltip metric="roasHalo" />,
        value: data?.formatted?.roas_halo || '-',
        compareValue: compareData?.formatted?.roas_halo,
        type: 'roasHalo'
      },
      {
        id: 'card-roas_overall',
        label: t('rm:metrics.roasOverall'),
        title: <TitleWithTooltip metric="roasOverall" />,
        value: (
          <RoasMetricCell
            metric="overall_roas"
            value={data?.formatted?.roas_overall || '-'}
            raw={data}
          />
        ),
        compareValue: compareData && (
          <RoasMetricCell
            metric="overall_roas"
            value={compareData.formatted?.roas_overall}
            raw={compareData}
          />
        ),
        type: 'roasOverall'
      },
      {
        id: 'card-16',
        label: t('rm:metrics.haloOrders'),
        title: <TitleWithTooltip metric="haloOrders" />,
        value: data?.formatted?.halo_orders || '-',
        compareValue: compareData?.formatted?.halo_orders,
        type: 'haloOrders'
      },
      {
        id: 'card-14',
        label: t('rm:metrics.haloRevenue'),
        title: <TitleWithTooltip metric="haloRevenue" />,
        value: data?.formatted?.halo_revenue || '-',
        compareValue: compareData?.formatted?.halo_revenue,
        type: 'haloRevenue'
      },
      {
        id: 'card-17',
        label: t('rm:metrics.haloItems'),
        title: <TitleWithTooltip metric="haloItems" />,
        value: data?.formatted?.halo_items || '-',
        compareValue: compareData?.formatted?.halo_items,
        type: 'haloItems'
      },
      {
        id: 'card-impression_share',
        label: t('rm:metrics.impressionShare'),
        title: (
          <TitleWithTooltip
            metric="impressionShare"
            alertIcon={
              data && (
                <ImpressionShareAlertIcon
                  value={data.impression_share}
                  settledDay={data.impression_share_settled_day}
                />
              )
            }
          />
        ),
        value: data?.formatted?.impressionShare || '-',
        compareValue: compareData?.formatted?.impressionShare,
        type: 'impressionShare'
      },
      {
        id: 'card-lost_is_rank',
        label: t('rm:metrics.lostIsRank'),
        title: (
          <TitleWithTooltip
            metric="lostIsRank"
            alertIcon={
              data && (
                <LostIsRankAlertIcon
                  value={data.lost_is_rank}
                  settledDay={data.impression_share_settled_day}
                />
              )
            }
          />
        ),
        value: data?.formatted?.lostIsRank || '-',
        compareValue: compareData?.formatted?.lostIsRank,
        type: 'lostIsRank'
      },
      {
        id: 'card-lost_is_budget',
        label: t('rm:metrics.lostIsBudget'),
        title: (
          <TitleWithTooltip
            metric="lostIsBudget"
            cardLabel={t('rm:metrics.lostIsBudget.card')}
            alertIcon={
              data && (
                <LostIsBudgetAlertIcon
                  value={data.lost_is_budget}
                  settledDay={data.impression_share_settled_day}
                />
              )
            }
          />
        ),
        value: data?.formatted?.lostIsBudget || '-',
        compareValue: compareData?.formatted?.lostIsBudget,
        type: 'lostIsBudget'
      },
      ...ntbCards,
      {
        id: 'card-07',
        label: t('rm:metrics.adCost'),
        title: <TitleWithTooltip metric="adCost" />,
        value: data?.formatted?.adcost || 0,
        compareValue: compareData?.formatted?.adcost,
        type: 'adCost'
      },
      {
        id: 'card-11',
        label: 'eCPM',
        title: 'eCPM',
        value: formatCurrencyValue(data?.ecpm),
        compareValue: formatCurrencyValue(compareData?.ecpm),
        type: 'ecpm'
      }
    ],
    [
      data,
      compareData,
      showClickAlert,
      isStandardStrategy,
      campaignAdType,
      ntbCards
    ]
  )

  const cardsByStrategy = useMemo(
    () =>
      cardsRaw.filter(item => {
        if (STANDARD_EXCLUSIVE_CARDS.has(item.type)) {
          return strategyType === 'standard'
        }

        if (item.type === 'adCost') return strategyType === 'objective_based'

        const availabilityKey = CARD_AVAILABILITY_KEY[item.type]

        return availabilityKey ? !!availableMetrics[availabilityKey] : true
      }),
    [cardsRaw, strategyType, availableMetrics]
  )

  const cardsEligible = useMemo(() => {
    if (
      adType &&
      CARDS_BY_AD_TYPE[adType] &&
      CARDS_BY_AD_TYPE[adType].type === 'subset'
    ) {
      return cardsByStrategy.filter(item =>
        (CARDS_BY_AD_TYPE[adType].cards as AvailableCards[]).includes(item.type)
      )
    }

    return cardsByStrategy
  }, [adType, cardsByStrategy])

  /**
   * A customização é salva por tipo de campanha: campanhas por objetivo têm
   * configuração própria; nas demais, uma por tipo de anúncio
   */
  const screenVariant =
    strategyType === 'objective_based' ? 'objective_based' : adType || 'all'

  return useCardVisibility({
    cards: cardsEligible,
    screenName: `campaign-view:${screenVariant}`,
    defaultVisible: DEFAULT_VISIBLE
  })
}
